import { PplDialogContainerComponent, PplDialogContainerData } from './dialog-container/dialog-container.component';
import { PplDialogMessageComponent } from './dialog-message/dialog-message.component';
import { appendComponentRefToPortalOutlet, detachComponentRefFromAppRef, PplDialogPopoverPortalOutletFactory } from './dialog-popover-portal-outlet';
import { PPL_DIALOG_DATA, PplDialogRef } from './dialog-ref';
import { uuid } from '../../../../utils/src';
import { PplPopoverPortalOutletFactory } from '../popover/popover-portal-outlet';
import { ComponentPortal, DomPortalOutlet, TemplatePortal } from '@angular/cdk/portal';
import { Location } from '@angular/common';
import {
  ApplicationRef,
  Injectable,
  Injector,
  TemplateRef
} from '@angular/core';
import * as Sentry from '@sentry/browser';
import { Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import type {
  ComponentType
} from '@angular/cdk/portal';
import type {
  ViewContainerRef
} from '@angular/core';
import type { ButtonType } from '../button/button.directive';
import type { PplDialogMessageData } from './dialog-message/dialog-message.component';
import type { PplDialogMoveData } from './dialog-ref';

@Injectable({
  providedIn: 'root'
})
export class PplDialogService {

  private appRef: ApplicationRef;
  private bodyPortalHost: DomPortalOutlet;
  private openDialogs: PplDialogRef[] = [];
  private static dialogOpened = new Subject<PplDialogRef>();

  static dialogOpened$ = PplDialogService.dialogOpened.asObservable();

  constructor(
    private injector: Injector,
    private location: Location
  ) { }

  open<C = any, D = any, R = any>(dialog: ComponentType<any> | TemplateRef<any>, options: PplDialogOpenOptions<D> = {}) {
    if (!this.bodyPortalHost) {
      if (!this.appRef) {
        this.appRef = this.injector.get<ApplicationRef>(ApplicationRef);
      }

      this.bodyPortalHost = new DomPortalOutlet(
        document.body,
        this.appRef,
        this.injector
      );
    }

    const afterClosed = new Subject<R>();
    const afterOpened = new Subject<void>();
    const afterMinimized = new Subject<void>();
    const afterMaximized = new Subject<void>();
    const dialogId = options.dialogId || uuid();
    const hideBackdrop = options.hideBackdrop ?? false;
    const minimizeEnabled = !!options.enableMinimize;
    let isDialogMinimized = false;

    const minimizeDialog = () => {
      if (!minimizeEnabled || isDialogMinimized) {
        return;
      }

      dialogPopoverPortalOutletFactory.minimizePopovers();
      detachComponentRefFromAppRef(this.appRef, containerRef);
      isDialogMinimized = true;
      afterMinimized.next();
    };

    const maximizeDialog = () => {
      if (!minimizeEnabled || !isDialogMinimized) {
        return;
      }

      this.appRef.attachView(containerRef.hostView);
      appendComponentRefToPortalOutlet(this.bodyPortalHost, containerRef);
      dialogPopoverPortalOutletFactory.maximizePopovers();
      isDialogMinimized = false;
      afterMaximized.next();
    };

    const close = (value?: R) => {
      this.openDialogs = this.openDialogs.filter(openDialog => openDialog !== dialogRef);
      containerRef.destroy();
      afterClosed.next(value);
      this.logCloseEvent(dialog);
    };

    const move = (data: PplDialogMoveData) => {
      containerRef.instance.move(data);
    };

    const containerData: PplDialogContainerData = {
      afterOpened,
      autoFocus: options.autoFocus === null ? null : options.autoFocus !== false,
      enableClose: options.enableClose === true,
      containerClass: options.containerClass,
      close,
      ...('zIndexIncrement' in options) ? { zIndexIncrement: options.zIndexIncrement } : {},
      initialPosition: options.initialPosition
    };

    const dialogRef: PplDialogRef<C, R> = {
      id: dialogId,
      afterClosed: () => afterClosed.asObservable(),
      afterOpened: () => afterOpened.asObservable(),
      close,
      componentInstance: undefined,
      canMinimize: () => minimizeEnabled,
      isMinimized: () => isDialogMinimized,
      afterMinimized: () => afterMinimized.asObservable(),
      afterMaximized: () => afterMaximized.asObservable(),
      minimize: minimizeEnabled
        ? minimizeDialog
        : null,
      maximize: minimizeEnabled
        ? maximizeDialog
        : null,
      move
    };

    const injector = options.injector || (options.viewContainerRef && options.viewContainerRef.injector) || this.injector;

    const containerInjector = Injector.create([{
      provide: PplDialogContainerData,
      useValue: containerData
    }], injector);

    const containerPortal = new ComponentPortal(PplDialogContainerComponent, null, containerInjector);
    const containerRef = this.bodyPortalHost.attachComponentPortal(containerPortal);

    const dialogPopoverPortalOutletFactory = new PplDialogPopoverPortalOutletFactory();

    const componentInjector = Injector.create([
      { provide: PplDialogRef, useValue: dialogRef },
      { provide: PPL_DIALOG_DATA, useValue: options.data || {} },
      { provide: PplPopoverPortalOutletFactory, useValue: dialogPopoverPortalOutletFactory }
    ], injector);

    const dialogPortal = (dialog instanceof TemplateRef)
      ? new TemplatePortal(dialog, null)
      : new ComponentPortal(dialog, null, componentInjector);

    const dialogPortalRef = containerRef.instance.dialogPortal.attach(dialogPortal);
    containerRef.instance.hideBackdrop = hideBackdrop;

    dialogRef.componentInstance = dialogPortalRef.instance;

    this.openDialogs.push(dialogRef);
    PplDialogService.dialogOpened.next(dialogRef);

    const locationSubscription = this.location.subscribe(() => {
      if (!isDialogMinimized) {
        close();
        locationSubscription.unsubscribe();
      }
    });

    this.logOpenEvent(dialog);

    return dialogRef;
  }

  closeAll() {
    [...this.openDialogs].reverse().forEach(openDialog => {
      openDialog.close();
    });
  }

  alert(options: PplDialogMessageOptions & { actionLabel?: string, cancelVisible?: boolean, cancelLabel?: string; }) {
    return this.open<PplDialogMessageComponent, PplDialogMessageData, true>(PplDialogMessageComponent, {
      data: {
        description: options.description,
        text: options.text,
        type: 'alert',
        actionLabel: options.actionLabel,
        cancelVisible: options.cancelVisible,
        cancelLabel: options.cancelLabel,
        width: options.width
      },
      zIndexIncrement: 3,
      autoFocus: false
    }).afterClosed();
  }

  confirm(options: PplDialogMessageOptions & { actionLabel?: string, actionButtonType?: ButtonType, cancelLabel?: string, onlyPositive?: boolean; }) {
    return this.open<PplDialogMessageComponent, PplDialogMessageData, boolean>(PplDialogMessageComponent, {
      data: {
        actionLabel: options.actionLabel,
        actionButtonType: options.actionButtonType,
        cancelLabel: options.cancelLabel,
        description: options.description,
        styleWhiteSpace: options.styleWhiteSpace,
        text: options.text,
        type: 'confirm',
        width: options.width
      },
      zIndexIncrement: 3,
      autoFocus: false
    }).afterClosed().pipe(filter(result => ((options.onlyPositive === false) ? true : result)));
  }

  info(options: PplDialogMessageOptions) {
    return this.open<PplDialogMessageComponent, PplDialogMessageData, true>(PplDialogMessageComponent, {
      data: {
        description: options.description,
        text: options.text,
        type: 'info'
      },
      zIndexIncrement: 3,
      autoFocus: false
    }).afterClosed();
  }

  yesNo(options: PplDialogMessageOptions & { displayCancel?: boolean, yesLabel?: string, noLabel?: string, noButtonType?: ButtonType; }) {
    return this.open<PplDialogMessageComponent, PplDialogMessageData, PplYesNoCancelResult>(PplDialogMessageComponent, {
      data: {
        description: options.description,
        noLabel: options.noLabel,
        noButtonType: options.noButtonType,
        text: options.text,
        type: options.displayCancel ? 'yesNoCancel' : 'yesNo',
        yesLabel: options.yesLabel
      },
      zIndexIncrement: 3,
      autoFocus: false
    }).afterClosed();
  }

  yesNoCancel(options: PplDialogMessageOptions & { yesLabel?: string, noLabel?: string, noButtonType?: ButtonType, noVisible?: boolean; }) {
    return this.open<PplDialogMessageComponent, PplDialogMessageData, PplYesNoCancelResult>(PplDialogMessageComponent, {
      data: {
        description: options.description,
        noLabel: options.noLabel,
        noButtonType: options.noButtonType,
        noVisible: options.noVisible ?? true,
        text: options.text,
        type: 'yesNoCancel',
        yesLabel: options.yesLabel
      },
      zIndexIncrement: 3,
      autoFocus: false
    }).afterClosed();
  }

  warning(options: PplDialogMessageOptions & { actionLabel?: string, actionButtonType?: ButtonType; cancelVisible?: boolean, cancelLabel?: string; }) {
    return this.open<PplDialogMessageComponent, PplDialogMessageData, true>(PplDialogMessageComponent, {
      data: {
        description: options.description,
        text: options.text,
        type: 'warning',
        width: options.width,
        actionLabel: options.actionLabel,
        actionButtonType: options.actionButtonType,
        cancelVisible: options.cancelVisible,
        cancelLabel: options.cancelLabel
      },
      zIndexIncrement: 3,
      autoFocus: false
    }).afterClosed();
  }

  hasOpenDialogs() {
    return this.openDialogs.length > 0;
  }

  findOpenDialog<C extends any>(dialogId: string) {
    return this.openDialogs.find(dialogRef => dialogRef.id === dialogId) as PplDialogRef<C>;
  }

  private logOpenEvent(dialog: any) {
    const selector = this.getComponentSelector(dialog);

    if (selector) {
      Sentry.addBreadcrumb({
        category: 'dialog',
        data: {
          event: 'open',
          selector
        }
      });
    }
  }

  private logCloseEvent(dialog: any) {
    const selector = this.getComponentSelector(dialog);

    if (selector) {
      Sentry.addBreadcrumb({
        category: 'dialog',
        data: {
          event: 'close',
          selector
        }
      });
    }
  }

  private getComponentSelector(dialog: any) {
    return dialog['ɵcmp']?.selectors?.[0] || null;
  }

}

export interface PplDialogOpenOptions<D> {
  autoFocus?: boolean | null;
  containerClass?: string;
  data?: D;
  injector?: Injector;
  viewContainerRef?: ViewContainerRef;
  enableClose?: boolean; // if true, ESC/click-on-backdrop closes the dialog
  zIndexIncrement?: number;
  enableMinimize?: boolean;
  dialogId?: string;
  hideBackdrop?: boolean;
  initialPosition?: {
    verticalAlign?: 'top' | 'center' | 'bottom';
    verticalOffset?: number;
  };
}

export interface PplDialogMessageOptions {
  description?: string;
  text: string;
  width?: string;
  zIndexIncrement?: number;
  styleWhiteSpace?: string;
}

export enum PplYesNoCancelResult {
  Yes = 'Yes',
  No = 'No',
  Cancel = 'Cancel'
}
